import {
  faHome,
  faUser
} from '@awesome.me/kit-5de77b2c02/icons/classic/regular'
import {
  faBars,
  faOctagon
} from '@awesome.me/kit-5de77b2c02/icons/classic/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cx from 'classnames'
import { FC } from 'react'
import { noop } from 'rxjs'
import bondsLogoLightTheme from '../../assets/images/bonds_logo_black.png'
import bondsLogoDarkTheme from '../../assets/images/logo_white_blue.png'
import config from '../../config'
import { isOpenFinRuntime } from '../../containers/OpenFin/helpers'
import { getIsSignedIn, getUserName } from '../../store/auth/selectors'
import { getMarketClosed } from '../../store/market/selectors'
import { getAllPendingUserOrders } from '../../store/order/selectors'
import { useAppDispatch, useAppSelector } from '../../store/types'
import { getUseLightTheme } from '../../store/userPreferences/selectors'
import { cancelAllWatchListOrdersAction } from '../../store/watchList/actions'
import { getWebSettingsLoaded } from '../../store/webSettings/selectors'
import BenchmarkContainer from '../BenchmarkData/BenchmarkContainer'
import MarketsClosed from '../MarketsClosed/MarketsClosed'
import UserSelector from '../UserSelector/UserSelector'
import styles from './header.module.scss'
import AdminMenu from './Menus/AdminMenu'
import ClientMenu from './Menus/ClientMenu'
import UserMenu from './Menus/UserMenu'

export interface Props {
  setSelectedTab: (st: string | null) => void
  isAdmin: boolean
}

const Header: FC<Props> = ({ setSelectedTab, isAdmin }) => {
  const isOpenFin = isOpenFinRuntime()
  const isSignedIn = useAppSelector(getIsSignedIn)
  const userName = useAppSelector(getUserName)
  const marketClosed = useAppSelector(getMarketClosed)
  const useLightTheme = useAppSelector(getUseLightTheme)
  const settingsLoaded = useAppSelector(getWebSettingsLoaded)

  const dispatch = useAppDispatch()

  const cancelAllOrders = () => {
    dispatch(cancelAllWatchListOrdersAction(undefined))
  }

  const bondsLogoTheme = useLightTheme
    ? bondsLogoLightTheme
    : bondsLogoDarkTheme

  const liveOrderCount = useAppSelector(getAllPendingUserOrders)?.length || ''
  const pendingSize = liveOrderCount ? 'fa-3x' : 'fa-lg'
  const cancelTitle = liveOrderCount ? `Cancel ${liveOrderCount} orders` : ''

  if (isOpenFin || !settingsLoaded) return <></>

  return (
    <div className={styles.header}>
      <a href="http://www.bonds.com" target="_blank" rel="noopener noreferrer">
        <img alt="bonds.com" src={bondsLogoTheme} style={{ height: '4rem' }} />
      </a>
      {!marketClosed && !isOpenFin && !isAdmin && isSignedIn && (
        <BenchmarkContainer />
      )}
      {marketClosed && (
        <MarketsClosed className={styles.marketStatus} isAdmin={isAdmin} />
      )}
      {isAdmin && <UserSelector />}
      <span className={styles.marketStatus}>{config.env}</span>
      {isSignedIn && (
        <div className={styles.rightSection}>
          <div className={styles.navTitle}>
            <span data-testid="email" className={styles.navIcon}>
              {userName}
            </span>
            <div className={styles.dropdownMenuWrapper}>
              <UserMenu />
            </div>
          </div>

          <div
            className={`fa-layers fa-fw ${pendingSize} ${styles.navIcon}`}
            onClick={cancelAllOrders}
            title={cancelTitle}
            data-testid="header-cancel-orders"
          >
            <FontAwesomeIcon
              icon={faOctagon}
              className={cx(styles.cancelAllButton)}
            />
            <span
              className={`fa-layers-text fa-inverse ${styles.liveOrderCount}`}
            >
              {liveOrderCount}
            </span>
          </div>

          <div
            className={styles.navTitle}
            onClick={() => setSelectedTab('Watchlist')}
          >
            <div title="Home">
              <FontAwesomeIcon className={styles.navIcon} icon={faHome} />
            </div>
          </div>
          {isAdmin && (
            <div className={styles.navTitle} data-testid="admin-menuicon">
              <FontAwesomeIcon className={styles.navIcon} icon={faUser} />
              <div className={styles.dropdownMenuWrapper}>
                <AdminMenu closeMenu={noop} handleNavigation={setSelectedTab} />
              </div>
            </div>
          )}

          <div className={styles.navTitle} data-testid="clientmenu-icon">
            <FontAwesomeIcon className={styles.navIcon} icon={faBars} />
            <div className={styles.dropdownMenuWrapper}>
              <ClientMenu closeMenu={noop} handleNavigation={setSelectedTab} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Header
